<template>
  <div :class="`theme-${theme}`" :style="{ '--primary-color': primaryColor }">
    <h2 class="title is-4">{{ title }}</h2>
    <p class="content">{{ message }}</p>
    <button
      class="button is-primary"
      :class="{ 'is-loading': loading }"
      @click="handleClick"
      :disabled="disabled"
    >
      {{ buttonText }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { BaseWebComponentProps } from '../types/webComponents'

// Props definieren
interface Props extends BaseWebComponentProps {
  title?: string
  message?: string
  buttonText?: string
  disabled?: boolean
  theme?: 'light' | 'dark'
  primaryColor?: string
}

// Props mit Standardwerten
const props = withDefaults(defineProps<Props>(), {
  title: 'Hallo Welt',
  message: 'Dies ist eine Beispiel Web-Komponente mit Bulma CSS',
  buttonText: 'Klick mich',
  disabled: false,
  theme: 'light',
  primaryColor: '#222f3e',
})

// Reaktive Daten
const loading = ref(false)
const clickCount = ref(0)

// Computed Properties
const buttonText = computed(() => {
  return clickCount.value > 0 ? `Geklickt: ${clickCount.value}x` : props.buttonText
})

// Events definieren
const emit = defineEmits<{
  click: [count: number]
  change: [data: { count: number; timestamp: number }]
}>()

// Event Handler
const handleClick = () => {
  if (props.disabled || loading.value) return

  loading.value = true

  setTimeout(() => {
    clickCount.value++
    loading.value = false

    // Events emittieren
    emit('click', clickCount.value)
    emit('change', {
      count: clickCount.value,
      timestamp: Date.now(),
    })
  }, 500)
}
</script>

<style scoped>
@import '../assets/main.css';

/* Primary Color Override mit CSS Custom Properties */
.button.is-primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: white !important;
}

.button.is-loading {
  color: transparent !important;
}

.button.is-primary:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
  border-color: color-mix(in srgb, var(--primary-color) 85%, black) !important;
}

.button.is-primary:focus {
  box-shadow: 0 0 0 0.125em color-mix(in srgb, var(--primary-color) 25%, transparent) !important;
}

/* Weißer Spinner für Primary Button */
.button.is-primary.is-loading::after {
  border-color: white !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
}
</style>
